import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { TealsThemeProvider, type TealsTheme } from '@teals/ui-web';
import { store, useAppSelector } from './store';
import { luminaLightTheme, luminaDarkTheme, GlobalStyles } from './theme';
import App from './App';

/**
 * Inner wrapper that reads the theme mode from Redux
 * and provides the matching styled-components theme.
 *
 * Uses TealsThemeProvider from @teals/ui-web as the styled-components
 * theme provider, passing in Lumina-specific overrides.
 * Lumina's own GlobalStyles inject CSS custom properties for
 * the light/dark theme switching used by all screens.
 */
const ThemedApp: React.FC = () => {
  const mode = useAppSelector((state) => state.theme.mode);
  const theme = mode === 'dark' ? luminaDarkTheme : luminaLightTheme;

  // Sync data-theme attribute for CSS custom properties
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  return (
    <TealsThemeProvider theme={theme as unknown as Partial<TealsTheme>}>
      <GlobalStyles />
      <App />
    </TealsThemeProvider>
  );
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <ThemedApp />
      </BrowserRouter>
    </Provider>
  </React.StrictMode>
);
